/**
 * sub-service-panel.js
 * Shared DOM plumbing for the "view options" sub-service expansion panel
 * used by any service-catalog category page (Design, Digital, Career,
 * Automation, Tutoring, ...). Sub-services are admin-configured in MongoDB
 * (Service.parentServiceId) and arrive on every category page already
 * grouped under their parent by GET /api/pricing/catalog's `subServices[]`
 * array — this module only owns rendering/toggling/basket-add, never the
 * catalog data itself. Pages keep their own service arrays and API-hydration
 * logic (see design-services.js / digital-services.js), styled via
 * css/components/sub-service-panel.css.
 *
 * Previously this render/toggle/add-to-basket logic (plus a runtime-injected
 * <style> tag) was copy-pasted independently into design-services.js and
 * digital-services.js. Consolidated here so every category page shares one
 * implementation instead of drifting copies, following the same shell
 * pattern as js/components/card-grid-modal.js.
 *
 * Usage from a category page (loaded as a <script type="module">):
 *   import { toggle, setSelectionMode, addToBasket, bindMoreToggles }
 *     from "../components/sub-service-panel.js";
 *
 *   function toggleSubServices(serviceId) {
 *     toggle({ serviceId, services: MY_SERVICES, namespace: "MyServices", basketCategory: "career" });
 *   }
 *   async function addSubServiceToBasket(parentId, subId) {
 *     await addToBasket({
 *       parentId, subId, services: MY_SERVICES,
 *       basketCategory: "career", namespace: "MyServices",
 *       getCurrency: () => state.currency, onToast: showToast,
 *     });
 *   }
 *   window.MyServices = { ..., toggleSubServices, addSubServiceToBasket, setSelectionMode };
 *
 * `namespace` must match the `window.<namespace>` object the page exposes
 * with toggleSubServices/setSelectionMode/addSubServiceToBasket methods —
 * the panel's own buttons call back into it via onclick attributes so no
 * per-instance event wiring is needed.
 */

"use strict";

let activeSelectionMode = "single"; // 'single' or 'multiple' — one panel open at a time site-wide

function escapeHtml(str) {
  return String(str ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[c]);
}

function formatPrice(usd) {
  return window.CurrencyService?.formatFromUSD?.(usd) ?? `$${usd}`;
}

function buildSubServiceItemHtml(sub, serviceId, namespace) {
  const price = formatPrice(sub.priceUSD);
  return (
    `<div class="sub-service-item" data-sub-id="${sub.id}" data-parent-id="${serviceId}">` +
    `<div class="sub-service-icon">${sub.icon || "📄"}</div>` +
    `<div class="sub-service-info">` +
    `<span class="sub-service-name">${escapeHtml(sub.name)}</span>` +
    `<button type="button" class="sub-service-more-btn" data-role="more-toggle">More</button>` +
    `<span class="sub-service-desc">${escapeHtml(sub.description || "")}</span>` +
    `</div>` +
    `<div class="sub-service-price"><span class="price-value">${price}</span></div>` +
    `<button class="sub-service-add" onclick="window.${namespace}.addSubServiceToBasket('${serviceId}', '${sub.id}')"><span>+</span></button>` +
    `</div>`
  );
}

/**
 * Detects sub-service names that are still clipped after the 2-line clamp
 * (very long single names / narrow viewports) and reveals a "More" toggle
 * for just that row so the user can expand it to see the full name next to
 * the already-visible price.
 */
function bindMoreToggles(panel) {
  if (!panel) return;
  panel.querySelectorAll(".sub-service-name").forEach((nameEl) => {
    const moreBtn = nameEl.parentElement?.querySelector(".sub-service-more-btn");
    if (!moreBtn) return;
    requestAnimationFrame(() => {
      if (nameEl.scrollHeight > nameEl.clientHeight + 1) {
        moreBtn.classList.add("visible");
      }
    });
    moreBtn.addEventListener("click", () => {
      const expanded = nameEl.classList.toggle("expanded");
      moreBtn.textContent = expanded ? "Less" : "More";
    });
  });
}

/**
 * Opens (or closes, if already open) the sub-services panel for a service.
 * @param {{serviceId:string, services:Array, namespace:string, basketCategory:string}} opts
 */
function toggle({ serviceId, services, namespace }) {
  const service = services.find((s) => String(s.id) === String(serviceId));
  if (!service || !service.hasSubServices) {
    console.warn(`[SubServicePanel:${namespace}] Service not found or has no sub-services:`, serviceId);
    return;
  }

  const card = document.querySelector(`[data-service-id="${serviceId}"]`);
  if (!card) {
    console.warn(`[SubServicePanel:${namespace}] Card not found for service:`, serviceId);
    return;
  }

  const existingPanel = card.querySelector(".sub-services-panel");
  const expandBtn = card.querySelector(".expand-btn");

  if (existingPanel) {
    existingPanel.remove();
    card.classList.remove("expanded");
    if (expandBtn) expandBtn.classList.remove("active");
    return;
  }

  document.querySelectorAll(".sub-services-panel").forEach((p) => p.remove());
  document.querySelectorAll(".service-card.expanded").forEach((c) => c.classList.remove("expanded"));
  document.querySelectorAll(".expand-btn.active").forEach((btn) => btn.classList.remove("active"));

  activeSelectionMode = "single";
  card.classList.add("expanded");
  if (expandBtn) expandBtn.classList.add("active");

  const panel = document.createElement("div");
  panel.className = "sub-services-panel";
  panel.dataset.parentId = serviceId;

  const itemsHtml = service.subServices
    .map((sub) => buildSubServiceItemHtml(sub, serviceId, namespace))
    .join("");

  panel.innerHTML =
    `<div class="sub-services-header">` +
    `<h4>Choose ${escapeHtml(service.name)} Options</h4>` +
    `<div class="selection-mode-toggle">` +
    `<button class="mode-dot single active" onclick="window.${namespace}.setSelectionMode('single', '${serviceId}')" title="Select one option"><span class="dot"></span></button>` +
    `<button class="mode-dot multiple" onclick="window.${namespace}.setSelectionMode('multiple', '${serviceId}')" title="Select multiple options"><span class="dot"></span><span class="dot"></span></button>` +
    `</div>` +
    `<button class="close-sub-panel" onclick="window.${namespace}.toggleSubServices('${serviceId}')">&times;</button>` +
    `</div>` +
    `<div class="selection-mode-label"><span class="mode-text">Single selection mode</span></div>` +
    `<div class="sub-services-grid">${itemsHtml}</div>`;

  card.appendChild(panel);
  bindMoreToggles(panel);
}

/** Switches single/multiple selection mode for the currently open panel. */
function setSelectionMode(mode, serviceId) {
  activeSelectionMode = mode;

  const panel = document.querySelector(`.sub-services-panel[data-parent-id="${serviceId}"]`);
  if (!panel) return;

  panel.querySelectorAll(".mode-dot").forEach((btn) => btn.classList.remove("active"));
  panel.querySelector(`.mode-dot.${mode}`)?.classList.add("active");

  const label = panel.querySelector(".mode-text");
  if (label) {
    label.textContent = mode === "single" ? "Single selection mode" : "Multiple selection mode";
  }
}

/**
 * Adds a sub-service to the basket via window.UnifiedBasket.
 * @param {{parentId:string, subId:string, services:Array, basketCategory:string,
 *   namespace:string, getCurrency?:()=>string, onToast?:(msg:string)=>void}} opts
 */
async function addToBasket({ parentId, subId, services, basketCategory, namespace, getCurrency, onToast }) {
  const service = services.find((s) => String(s.id) === String(parentId));
  if (!service || !service.subServices) return;

  const subService = service.subServices.find((s) => String(s.id) === String(subId));
  if (!subService) return;

  const currency = getCurrency ? getCurrency() : "USD";
  const flatRate = currency === "NGN" ? subService.priceNGN : subService.priceUSD;
  const itemId = `${parentId}-${subId}`;

  // Academic sub-services are priced per 100 words, same rate model as the
  // full order modal (see academic-services.js addToBasketFromModal) — but
  // the quick-add path has no tier/level/package selector, so this scales
  // the flat per-100-word rate directly by the parent's default word count
  // instead of leaving the item stuck at the flat rate (which used to price
  // e.g. a 15,000-word Dissertation at its ~£4 per-100-word rate instead of
  // ~£600). UnifiedBasket.addItem() does this same scaling again whenever
  // pricePerWord is present, so this only needs to seed the right numbers.
  const words = service.defaultWordCount > 0 ? service.defaultWordCount : undefined;
  const isWordBased = basketCategory === "academic" && words > 0 && flatRate > 0;
  const pricePerWord = isWordBased ? flatRate / 100 : undefined;
  const price = isWordBased ? Math.max(flatRate, words * pricePerWord) : flatRate;
  const accompanying = Array.isArray(subService.accompanyingServices)
    ? subService.accompanyingServices
    : [];

  if (activeSelectionMode === "single") {
    const panel = document.querySelector(`.sub-services-panel[data-parent-id="${parentId}"]`);
    if (panel) {
      panel.querySelectorAll(".sub-service-item.in-basket").forEach((item) => {
        const existingSubId = item.dataset.subId;
        if (existingSubId !== subId) {
          const existingItemId = `${parentId}-${existingSubId}`;
          if (window.UnifiedBasket) window.UnifiedBasket.removeItem(existingItemId);
          item.classList.remove("in-basket");
          const addBtn = item.querySelector(".sub-service-add");
          if (addBtn) addBtn.innerHTML = "<span>+</span>";
        }
      });
    }
  }

  if (window.UnifiedBasket) {
    await window.UnifiedBasket.addItem({
      id: itemId,
      name: `${service.name}: ${subService.name}`,
      category: basketCategory,
      price,
      icon: subService.icon,
      parentService: parentId,
      subServiceId: subId,
      // Sub-service quick-adds have no word-count picker of their own — carry
      // the parent's admin-configured defaultWordCount through so the basket
      // widget's own Word Count field reflects it (see unified-basket.js
      // addItem()'s basket.wordCount sync), same as the full order-modal path.
      words,
      // Needed so both the add-time price above and any later word-count
      // edit in the basket widget (updateWordCount()) use the same rate.
      ...(isWordBased ? { pricePerWord, wordRate: pricePerWord, basePrice: flatRate } : {}),
      // Sub-service quick-adds have no tier picker of their own — inherit
      // whatever complexity the user already set in the basket widget.
      complexity: window.UnifiedBasket.getComplexity?.()?.level || "standard",
    });

    // A sub-service can carry its own admin-configured accompaniments (e.g.
    // "Dissertation with presentation" -> Presentation Design) — add each as
    // its own independent, separately-priced basket line, same as the
    // full-modal add path does. Without this the accompaniment is silently
    // dropped for every quick-add sub-service across every category page.
    for (const a of accompanying) {
      const hasPromo =
        a.originalPrice > 0 && a.salePrice > 0 && a.originalPrice > a.salePrice;
      await window.UnifiedBasket.addItem({
        id: `${a.code}-accompaniment-${Date.now()}`,
        category: (a.category || basketCategory || "").toLowerCase(),
        name: a.name,
        price: a.showingPrice ?? a.basePrice ?? 0,
        originalPrice: a.originalPrice || 0,
        salePrice: a.salePrice || 0,
        accompanimentOf: itemId,
        accompanimentSourceService: parentId,
        accompanimentSourceServiceName: `${service.name}: ${subService.name}`,
        details: hasPromo
          ? `Includes ${a.name}'s existing discount`
          : `Accompanies ${subService.name}`,
      });
    }
  }

  const subItem = document.querySelector(`.sub-service-item[data-sub-id="${subId}"]`);
  if (subItem) {
    subItem.classList.add("in-basket");
    const addBtn = subItem.querySelector(".sub-service-add");
    if (addBtn) addBtn.innerHTML = "<span>📜</span>";
  }

  if (onToast) {
    onToast(
      accompanying.length
        ? `${subService.name} + ${accompanying.map((a) => a.name).join(" + ")} added to basket!`
        : `${subService.name} added to basket`,
    );
  }

  if (activeSelectionMode === "single") {
    setTimeout(() => {
      toggle({ serviceId: parentId, services, namespace }); // closes the panel
    }, 500); // brief delay so the user sees the checkmark
  }
}

window.SubServicePanel = { toggle, setSelectionMode, addToBasket, bindMoreToggles };

export { toggle, setSelectionMode, addToBasket, bindMoreToggles };
